import React, { useCallback, useEffect, useRef } from 'react';
import { Field, useFormikContext } from 'formik';
import _ from 'lodash';
import { Country, State as StateEnum } from '@tradingblock/types';
import { TextboxField, SelectDropdownField } from './';
import { FormGroup } from '../../basic';
import { triggerUspsVerification } from '../../../utilities/validation';
const formatedValue = (value) => {
    return value
        ? value
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.substring(1))
            .join(' ')
        : '';
};
// Function to get USPS response from storage
const getUspsResponseFromStorage = (prefix) => {
    const uspsResponseFromStorage = sessionStorage.getItem(`${prefix}.uspsResponse`);
    return uspsResponseFromStorage && uspsResponseFromStorage !== 'undefined' ? uspsResponseFromStorage : null;
};
export const AddressField = ({ id, labelPrefix, countryProps, addr1Help, stateProps, field, labelPrefixOnlyForAddressLines, tradingApiUrl, apiToken, }) => {
    const { value } = field;
    const country = _.get(value, `${id}.country`);
    const uspsResponse = getUspsResponseFromStorage(id);
    const context = useFormikContext();
    const debounceTimerRef = useRef(null);
    let uspsResponseValues, city, state, zip5, zip4, postalCode, uspsAddress1, uspsAddress2;
    if (uspsResponse) {
        uspsResponseValues = JSON.parse(uspsResponse);
        city = formatedValue(uspsResponseValues.city);
        state = uspsResponseValues.state;
        zip5 = formatedValue(uspsResponseValues.zip5 || '');
        zip4 = formatedValue(uspsResponseValues.zip4 || '');
        // Combine zip5 and zip4 into postalCode format (zip5-zip4)
        postalCode = zip4 ? `${zip5}-${zip4}` : zip5;
        uspsAddress1 = formatedValue(uspsResponseValues.uspsAddress1 || '');
        // New API doesn't return address2 separately, so it will be null or undefined
        uspsAddress2 = uspsResponseValues.uspsAddress2 ? formatedValue(uspsResponseValues.uspsAddress2) : '';
    }
    // Check if an error is a USPS formatting error
    const isUspsFormattingError = (error) => {
        return !!error && error.includes('Please utilize the USPS provided formatting');
    };
    // Get USPS formatting errors to display after the button
    const getUspsFormattingErrors = () => {
        const errors = [];
        const address1Error = context.errors && context.errors[`${id}.address1`];
        const address2Error = context.errors && context.errors[`${id}.address2`];
        if (isUspsFormattingError(address1Error)) {
            errors.push(address1Error);
        }
        if (isUspsFormattingError(address2Error)) {
            errors.push(address2Error);
        }
        return errors;
    };
    const uspsFormattingErrors = getUspsFormattingErrors();
    // Debounced USPS validation effect
    useEffect(() => {
        // Only run if trading API URL is provided
        if (!tradingApiUrl) {
            return;
        }
        // Get current address values from form
        const address1 = _.get(context.values, `${id}.address1`);
        const address2 = _.get(context.values, `${id}.address2`);
        const city = _.get(context.values, `${id}.city`);
        const state = _.get(context.values, `${id}.state`);
        const postalCode = _.get(context.values, `${id}.postalCode`);
        const country = _.get(context.values, `${id}.country`);
        // Clear any existing debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        // Set up debounced validation (500ms)
        debounceTimerRef.current = setTimeout(async () => {
            // Check if validation should run (US country, required fields present)
            if (address1 &&
                city &&
                state &&
                country === Country.UnitedStatesOfAmerica &&
                state !== StateEnum.PuertoRico &&
                tradingApiUrl) {
                try {
                    const address = {
                        address1,
                        address2,
                        city,
                        state,
                        postalCode,
                        country,
                    };
                    // Use the same prefix format as getUspsResponseFromStorage
                    const prefix = `${id}.`;
                    await triggerUspsVerification(address, prefix, tradingApiUrl, apiToken);
                    // Trigger Formik validation to update error states
                    context.validateForm();
                }
                catch (err) {
                    console.error('Error in debounced USPS validation:', err);
                }
            }
            else if ((!address1 || !city) && tradingApiUrl) {
                // Clear USPS storage if fields are cleared
                sessionStorage.setItem(`${id}.uspsResponse`, 'undefined');
            }
        }, 500);
        // Cleanup function
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        _.get(context.values, `${id}.address1`),
        _.get(context.values, `${id}.address2`),
        _.get(context.values, `${id}.city`),
        _.get(context.values, `${id}.state`),
        _.get(context.values, `${id}.postalCode`),
        _.get(context.values, `${id}.country`),
        id,
        tradingApiUrl,
        apiToken,
    ]);
    const getTextboxFieldProps = useCallback((field) => {
        const fieldProps = {
            component: TextboxField,
            id: `${id}.${field}`,
            defaultLabelId: `address.${field}`,
        };
        if (field === 'state') {
            fieldProps.defaultLabelId = `address.province`;
        }
        const shouldApplyPrefix = !labelPrefixOnlyForAddressLines || field === 'address1' || field === 'address2';
        if (labelPrefix && shouldApplyPrefix) {
            fieldProps.labelPrefix = labelPrefix;
        }
        // Suppress USPS formatting errors for address1 and address2 fields
        // They will be displayed after the button instead
        if ((field === 'address1' || field === 'address2') && context.errors) {
            const fieldError = context.errors[`${id}.${field}`];
            if (isUspsFormattingError(fieldError)) {
                fieldProps.suppressError = true;
            }
        }
        return fieldProps;
    }, [id, labelPrefix, labelPrefixOnlyForAddressLines, context.errors]);
    const showUseProvidedButton = () => {
        return (context.errors &&
            ((uspsAddress1 && context.errors[`${id}.address1`]) ||
                (uspsAddress2 && context.errors[`${id}.address2`]) ||
                (city && context.errors[`${id}.city`]) ||
                (state && context.errors[`${id}.state`]) ||
                (postalCode && context.errors[`${id}.postalCode`])));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(FormGroup, null,
            React.createElement(Field, Object.assign({ component: SelectDropdownField, id: `${id}.country` }, countryProps, { autoComplete: "country" }))),
        React.createElement(FormGroup, { help: addr1Help },
            React.createElement(Field, Object.assign({}, getTextboxFieldProps('address1'), { autoComplete: "street-address" }))),
        React.createElement(FormGroup, null,
            React.createElement(Field, Object.assign({}, getTextboxFieldProps('address2'), { autoComplete: "address-line2" }))),
        React.createElement(FormGroup, null,
            React.createElement(Field, Object.assign({}, getTextboxFieldProps('city'), { autoComplete: "address-level2" }))),
        React.createElement(FormGroup, null,
            (country === Country.UnitedStatesOfAmerica || country === '') && (React.createElement(React.Fragment, null,
                React.createElement(Field, Object.assign({ component: SelectDropdownField, id: `${id}.state` }, stateProps, { autoComplete: "address-level1" })))),
            country !== Country.UnitedStatesOfAmerica && country !== '' && (React.createElement(React.Fragment, null,
                React.createElement(Field, Object.assign({}, getTextboxFieldProps('state'), { autoComplete: "address-level1" }))))),
        React.createElement(FormGroup, null,
            React.createElement(Field, Object.assign({}, getTextboxFieldProps('postalCode'), { autoComplete: "postal-code" }))),
        showUseProvidedButton() && (React.createElement(FormGroup, null,
            React.createElement("button", { className: "btn btn-block btn-outline-warning", onClick: e => {
                    e.preventDefault();
                    console.log('Use USPS Address button clicked:', {
                        uspsAddress1,
                        uspsAddress2,
                        city,
                        state,
                        postalCode,
                        uspsResponseValues,
                    });
                    const updatedValues = _.cloneDeep(context.values);
                    // Always update all address fields when using USPS provided address
                    // This ensures fields are updated even if they don't have errors
                    // and clears address2 if USPS doesn't provide it
                    if (uspsAddress1) {
                        _.set(updatedValues, `${id}.address1`, uspsAddress1);
                    }
                    // Set address2 to the USPS value (or empty string if not provided)
                    // Note: New API doesn't return address2 separately, so it will be empty
                    _.set(updatedValues, `${id}.address2`, uspsAddress2 || '');
                    if (city) {
                        _.set(updatedValues, `${id}.city`, city);
                    }
                    if (state) {
                        _.set(updatedValues, `${id}.state`, state);
                    }
                    if (postalCode) {
                        _.set(updatedValues, `${id}.postalCode`, postalCode);
                    }
                    console.log('Updated values for address:', {
                        address1: _.get(updatedValues, `${id}.address1`),
                        address2: _.get(updatedValues, `${id}.address2`),
                        city: _.get(updatedValues, `${id}.city`),
                        state: _.get(updatedValues, `${id}.state`),
                        postalCode: _.get(updatedValues, `${id}.postalCode`),
                    });
                    // Update Formik's form values
                    context.setValues(updatedValues);
                    // Mark fields as touched to trigger validation
                    context.setFieldTouched(`${id}.address1`, true);
                    context.setFieldTouched(`${id}.address2`, true);
                    context.setFieldTouched(`${id}.city`, true);
                    context.setFieldTouched(`${id}.state`, true);
                    context.setFieldTouched(`${id}.postalCode`, true);
                    // Trigger validation after a short delay to ensure values are set
                    setTimeout(() => {
                        context.validateForm();
                    }, 100);
                } }, "Use USPS provided address"),
            uspsFormattingErrors.length > 0 && (React.createElement("div", { style: { marginTop: '0.5rem' } }, uspsFormattingErrors.map((error, index) => (React.createElement("span", { key: index, className: "error", style: { display: 'block' } }, error)))))))));
};
//# sourceMappingURL=Address.js.map